'use client'

import React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import {
  AUTO_NAME,
  AUTO_SUBLABEL,
  badgeSpoken,
  badgeText,
  isNavItemActive,
  isNeedsBadge,
  type NavItem,
} from '@/lib/navigation'
import { Logomark } from '@/components/brand'
import { NavItemLabel } from '@/components/layout/NavItemLabel'
import { useVisibleNavSections } from '@/components/layout/Sidebar'
import { LOGOUT_HREF } from '@/components/layout/SidebarPersona'
import { PersonaMenu } from '@/components/shell'
import { useNavBadges } from '@/hooks/useShellData'

interface NavLinkProps {
  item: NavItem
  onClick?: () => void
  badge?: number
}

function NavLink({ item, onClick, badge }: NavLinkProps) {
  const { href, icon: Icon, badge: badgeKey } = item
  const pathname = usePathname()
  const isActive = isNavItemActive(pathname, href)
  const count = badgeText(badge)
  const needs = isNeedsBadge(badgeKey)

  return (
    <Link
      href={href}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'flex items-center gap-3 rounded-xl px-4 py-3',
        'text-base font-medium transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      <Icon
        strokeWidth={1.5}
        aria-hidden='true'
        className={cn('h-5 w-5 shrink-0', isActive ? 'text-primary-foreground' : 'text-muted-foreground')}
      />
      <span className='min-w-0'>
        <NavItemLabel item={item} />
      </span>
      {count && (
        <span
          className={cn(
            'ml-auto rounded-full px-2 py-0.5 text-xs font-semibold',
            needs ? 'bg-needs-you text-primary-foreground' : 'bg-brand-cornflower/25 text-foreground'
          )}
        >
          <span aria-hidden='true'>{count}</span>
          <span className='sr-only'>{badgeSpoken(badgeKey, count)}</span>
        </span>
      )}
    </Link>
  )
}

interface MobileSidebarProps {
  isOpen: boolean
  onClose: () => void
}

export function MobileSidebar({ isOpen, onClose }: MobileSidebarProps) {
  const sections = useVisibleNavSections()
  const badges = useNavBadges()
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side='left' className='w-72 overflow-y-auto p-0 md:hidden'>
        {/* pr-10 keeps the title clear of the sheet's close button. */}
        <SheetHeader className='flex h-20 flex-row items-center justify-between border-b border-border pl-4 pr-10'>
          <Link
            href='/'
            className='flex min-w-0 items-center gap-3 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            onClick={onClose}
          >
            <Logomark variant='dark' size={40} alt='' className='shadow-soft' />
            <span className='flex min-w-0 flex-col text-left'>
              <SheetTitle className='line-clamp-2 font-display text-base font-semibold leading-tight tracking-tight text-foreground'>
                {AUTO_NAME}
              </SheetTitle>
              <SheetDescription className='text-xs text-muted-foreground'>{AUTO_SUBLABEL}</SheetDescription>
            </span>
          </Link>
        </SheetHeader>

        {/* "Viewing as" lives here on phones, where the header has no room for it. */}
        <div className='border-b border-border p-4 sm:hidden'>
          <PersonaMenu showLabel />
        </div>

        <nav className='flex-1 p-4' aria-label='Mobile navigation'>
          {sections.map((section, sectionIndex) => (
            <div key={section.title} className={cn(sectionIndex > 0 && 'mt-6')}>
              <p className='mb-2 px-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground'>
                {section.title}
              </p>
              <ul className='space-y-1'>
                {section.items.map((item) => (
                  <li key={item.id}>
                    <NavLink item={item} onClick={onClose} badge={item.badge ? badges[item.badge] : undefined} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className='border-t border-border p-4'>
          <a
            href={LOGOUT_HREF}
            className='flex w-full items-center gap-3 rounded-xl px-4 py-3 text-base font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          >
            <Icons.logout className='h-5 w-5' strokeWidth={1.5} aria-hidden='true' />
            Sign out
          </a>
        </div>
      </SheetContent>
    </Sheet>
  )
}
